import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {Formik, useFormikContext} from "formik";
import {isEmpty} from "lodash";

import FilterButton from "../../../utilities/Buttons/FilterBtn/FilterButton";
import {checkEmptyFields} from "../../../utilities/checkEmptyFields";
import useSessionFilters from "../../../utilities/hooks/useSessionFilters";
import {
  formChildrenWithPermissions,
  UI_PERMISSION_TYPE,
} from "../../adminForms/Ba3aSettings/tabs/PositionsSettings/useRoutePermissions";

import ChosenFiltersLine from "./ChosenFiltersLine";
import {FiltersContext} from "./filters.context";
import {useFiltersContext} from "./useSessionFiltersContext";

export const Filters = ({
  onFilterChange = () => {},
  onFilterDelete = () => {},
  children,
  className,
  style,
  defaultFilters,
  enableTime = false,
  AdditionalBtn = <></>,
  disabledButton,
  uiPermissionType = UI_PERMISSION_TYPE.READABLE,
}) => {
  const {filters} = useFiltersContext();

  return (
    <Formik initialValues={{...filters}} validateOnChange enableReinitialize>
      <InnerContainer
        onFilterChange={onFilterChange}
        onFilterDelete={onFilterDelete}
        className={className}
        style={style}
        defaultFilters={defaultFilters}
        enableTime={enableTime}
        AdditionalBtn={AdditionalBtn}
        disabledButton={disabledButton}
        uiPermissionType={uiPermissionType}
      >
        {children}
      </InnerContainer>
    </Formik>
  );
};

export const SessionFiltersContainer = ({window, onFilterChange, defaultFilters, ...props}) => {
  const {
    filters: sessionF,
    setFilters: setSessionF,
    clearFilters: clearSessionFilters,
    initialFilters,
  } = useSessionFilters(window);

  const [filters, setFiltersValues] = useState(initialFilters || defaultFilters || {});
  const [formikValues, setFormikValues] = useState(initialFilters || defaultFilters || {});

  useEffect(() => {
    setFiltersValues(initialFilters || defaultFilters);
  }, [JSON.stringify(initialFilters ?? {}), JSON.stringify(defaultFilters ?? {})]);

  const setFilters = filters => {
    const newFilters = !isEmpty(filters) ? filters : defaultFilters ? defaultFilters : {};

    setFiltersValues(newFilters);
    isEmpty(filters) && !defaultFilters ? clearSessionFilters() : setSessionF(newFilters);
    if (onFilterChange) onFilterChange(newFilters);
  };

  return (
    <FiltersContext.Provider
      value={{
        filters,
        setFilters,
        sessionF,
        setSessionF,
        setFormikValues,
        clearSessionFilters,
        formValues: formikValues,
        window,
      }}
    >
      {props.children}
    </FiltersContext.Provider>
  );
};

const InnerContainer = ({
  children,
  onFilterChange,
  onFilterDelete,
  className,
  enableTime,
  AdditionalBtn,
  disabledButton,
  uiPermissionType,
}) => {
  const {t} = useTranslation();
  const {filters, setFilters, setFormikValues} = useFiltersContext();
  const {setValues, values} = useFormikContext();

  // const [selectedFiltersNames, setSelectedFiltersName] = useState({});

  const cleanedForIcon = (() => {
    if (!filters) return {};
    const acc = {};
    for (const [k, v] of Object.entries(filters)) {
      if (v != null && v !== "") acc[k] = v;
    }
    return acc;
  })();
  const areFiltersDefined = !isEmpty(cleanedForIcon);

  useEffect(() => {
    setFormikValues(values);
  }, [values]);

  // useEffect(() => {
  //   if (isEmpty(filters) && !defaultFilters) {
  //     return;
  //   }
  //   const savedStr = generateSelectedFiltersNames(
  //     children,
  //     filters,
  //     isEmpty(filters) && defaultFilters ? defaultFilters : filters,
  //   );
  //   setSelectedFiltersName(savedStr);
  // }, [JSON.stringify(filters), JSON.stringify(defaultFilters), children]);

  function onFilter(newFilters) {
    // resetForm();
    const params = checkEmptyFields({...filters, ...newFilters, ...values});
    onFilterChange(params);
    if (Object.keys(params).length > 0) {
      setFilters(params);
    }
    if (newFilters && Object.keys(params).length === 0) {
      setFilters(null);
    }
  }

  const delFilters = () => {
    const resetValues = Object.keys(values).reduce((acc, key) => {
      acc[key] = typeof values[key] === "number" ? 0 : "";
      return acc;
    }, {});

    setValues(resetValues);
    setFilters(null);
    onFilterDelete();
  };

  const generateSelectedFiltersNames = (fields, filters_) => {
    let names = {};
    React.Children.forEach(fields, child => {
      if (React.isValidElement(child)) {
        if (typeof child.type === "function" && child.props.name && Object.keys(filters_).includes(child.props.name)) {
          let field = child.props;
          names[field?.name] = {
            label: field.label,
            value: field?.options
              ? field?.options?.find(option => option.value === filters_[child.props.name])?.title
              : filters_[child.props.name],
          };
          if (child.type.name === "DateInput") {
            names[field?.name].type = "date";
          }
        } else {
          names = {...names, ...generateSelectedFiltersNames(child.props.children, filters_)};
        }
      }
    });
    return names;
  };

  const selectedFiltersNames = generateSelectedFiltersNames(
    children,
    filters ?? {},
    // isEmpty(initialFilters) && defaultFilters ? defaultFilters : initialFilters,
  );

  const childrenWithPermissions = formChildrenWithPermissions({children, uiPermissionType});

  return (
    <>
      <div
        className={`fc jcsb aistr w100 g1 autolabel nowrap filtersTopContainer ${className || ""}`}
        style={{marginTop: "0.5rem", marginBottom: "0.25rem"}}
      >
        <div className="filtersTitle">{t("Filters")}</div>
        <div style={{paddingRight: "2.5em", paddingLeft: "5px"}} className="asstr">
          <FilterButton
            onFilter={onFilter}
            delFilter={delFilters}
            questionTitle={t("Filter")}
            areFiltersDefined={areFiltersDefined}
            disabled={disabledButton}
          >
            {AdditionalBtn}
          </FilterButton>
        </div>

        {childrenWithPermissions}
      </div>
      <div className="w100">
        <ChosenFiltersLine filterNames={selectedFiltersNames} onFilterDelete={onFilterDelete} enableTime={enableTime} />
      </div>
    </>
  );
};

export const withSessionFilterContainer =
  ({window, onFilterChange = () => {}}) =>
  Component => {
    const WrappedComponent = props => {
      return (
        <SessionFiltersContainer window={window} onFilterChange={onFilterChange}>
          <Component {...props} />
        </SessionFiltersContainer>
      );
    };

    WrappedComponent.displayName = `withSessionFilterContainer(${
      Component.displayName || Component.name || "Component"
    })`;

    return WrappedComponent;
  };
